import * as i0 from '@angular/core';
import { Input, ContentChild, ChangeDetectionStrategy, Component, NgModule } from '@angular/core';
import { MappedStyles } from '@aposin/ng-aquila/core';
import { NxIconComponent } from '@aposin/ng-aquila/icon';
import * as i1 from '@angular/cdk/a11y';
const _c0 = ["*"];
const MAPPING = {
  black: 'nx-link--black',
  negative: 'nx-link--negative',
  text: 'nx-link--text',
  'icon-right': 'nx-link--icon-right',
  'icon-only': 'nx-link--icon-only'
};
const DEFAULT_CLASSES = ['nx-link'];
/**
 * `Input('nxStyle') classNames` sets the style of the link, thereby altering the visual appearance.
 * You can use any combination of 'black', 'icon-right', 'icon-only', 'negative' or 'text'.
 */
class NxLinkComponent extends MappedStyles {
  /** Sets the size of the link. Default: 'small'. */
  set size(value) {
    if (this._size !== value) {
      this._size = value;
      this._cdr.markForCheck();
    }
  }
  get size() {
    return this._size;
  }
  constructor(_elementRef, _renderer, _cdr, _focusMonitor) {
    super(MAPPING, _elementRef, _renderer, DEFAULT_CLASSES);
    this._cdr = _cdr;
    this._focusMonitor = _focusMonitor;
    this._size = 'small';
  }
  ngAfterContentInit() {
    // Add a specific link class that the css gets more specific than the nx-icon css
    const icons = this.elementRef.nativeElement.querySelectorAll('nx-icon');
    for (let i = 0; i < icons.length; i++) {
      this._renderer.addClass(icons[i], 'nx-link__icon');
    }
  }
  ngAfterViewInit() {
    this._focusMonitor.monitor(this._elementRef, true);
  }
  ngOnDestroy() {
    this._focusMonitor.stopMonitoring(this._elementRef);
  }
  /**
   * @docs-private
   * getter used for the modal component as a quickfix
   * since button got changed from directive to component the reference used by the ngOpenModelOnClick directive
   * for nxButtons is a reference to component instance instead of an element reference. As a workaround we need a
   * way to reach the elementRef of the component until the modal gets refactored.
   */
  get elementRef() {
    return this._elementRef;
  }
  static {
    this.ɵfac = function NxLinkComponent_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NxLinkComponent)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.Renderer2), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(i1.FocusMonitor));
    };
  }
  static {
    this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: NxLinkComponent,
      selectors: [["nx-link"]],
      contentQueries: function NxLinkComponent_ContentQueries(rf, ctx, dirIndex) {
        if (rf & 1) {
          i0.ɵɵcontentQuery(dirIndex, NxIconComponent, 5);
        }
        if (rf & 2) {
          let _t;
          i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.icon = _t.first);
        }
      },
      hostVars: 6,
      hostBindings: function NxLinkComponent_HostBindings(rf, ctx) {
        if (rf & 2) {
          i0.ɵɵclassProp("nx-link--xsmall", ctx.size === "xsmall")("nx-link--small", ctx.size === "small")("nx-link--large", ctx.size === "large");
        }
      },
      inputs: {
        classNames: [0, "nxStyle", "classNames"],
        size: "size"
      },
      features: [i0.ɵɵInheritDefinitionFeature],
      ngContentSelectors: _c0,
      decls: 1,
      vars: 0,
      template: function NxLinkComponent_Template(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵprojectionDef();
          i0.ɵɵprojection(0);
        }
      },
      styles: ["[_nghost-%COMP%]{color:var(--link-color);display:inline-block;cursor:pointer;position:relative;text-decoration:none;transition:color .2s ease,box-shadow .2s ease;hyphens:none}[_nghost-%COMP%]:hover{color:var(--link-hover-color)}[_nghost-%COMP%]  a:active{color:var(--link-active-color)}[_nghost-%COMP%]  a{display:flex;align-items:flex-start}.nx-link--xsmall[_nghost-%COMP%]{font-size:var(--link-xsmall-font-size);line-height:var(--link-xsmall-line-height);font-weight:var(--link-xsmall-font-weight);letter-spacing:var(--link-xsmall-letter-spacing)}.nx-link--small[_nghost-%COMP%]{font-size:var(--link-small-font-size);line-height:var(--link-small-line-height);font-weight:var(--link-small-font-weight);letter-spacing:var(--link-small-letter-spacing)}.nx-link--large[_nghost-%COMP%]{font-size:var(--link-large-font-size);line-height:var(--link-large-line-height);font-weight:var(--link-large-font-weight);letter-spacing:var(--link-large-letter-spacing)}.cdk-keyboard-focused[_nghost-%COMP%]     a{border-radius:4px;outline:0;box-shadow:var(--focus-box-shadow)}@media screen and (forced-colors: active),(forced-colors: active){.cdk-keyboard-focused[_nghost-%COMP%]     a{box-shadow:0 0 0 2px background,0 0 0 6px CanvasText;outline:4px solid CanvasText;outline-offset:2px}}.nx-link--negative[_nghost-%COMP%]{color:var(--negative)}.nx-link--negative[_nghost-%COMP%]:hover{color:var(--negative-01)}.nx-link--negative[_nghost-%COMP%]  a:active{color:var(--negative-02)}.nx-link--negative[_nghost-%COMP%]  a[disabled], .nx-link--negative[_nghost-%COMP%]  a[aria-disabled]{color:var(--negative-dimmed)}.nx-link--black[_nghost-%COMP%]{color:var(--link-black-color)}.nx-link--black[_nghost-%COMP%]:hover{color:var(--link-black-hover-color)}.nx-link--black[_nghost-%COMP%]  a:active{color:var(--link-black-active-color)}.nx-link--black[_nghost-%COMP%]  a[disabled], .nx-link--black[_nghost-%COMP%]  a[aria-disabled]{color:var(--link-black-disabled-color)}.nx-link--text[_nghost-%COMP%]{font-weight:700;text-transform:none;letter-spacing:normal;border-radius:4px;font-size:inherit;line-height:inherit;display:inline}.nx-link--text[_nghost-%COMP%]     a{display:inline}.nx-link--text[_nghost-%COMP%]     nx-icon.nx-link__icon{vertical-align:bottom}[_nghost-%COMP%]  .c-icon:before, [_nghost-%COMP%]  nx-icon.nx-link__icon:before{line-height:inherit;font-size:inherit}.nx-link--xsmall[_nghost-%COMP%]     .c-icon, .nx-link--xsmall[_nghost-%COMP%]     nx-icon.nx-link__icon{font-size:var(--link-xsmall-icon-size)}.nx-link--small[_nghost-%COMP%]     .c-icon, .nx-link--small[_nghost-%COMP%]     nx-icon.nx-link__icon{font-size:var(--link-small-icon-size)}.nx-link--large[_nghost-%COMP%]     .c-icon, .nx-link--large[_nghost-%COMP%]     nx-icon.nx-link__icon{font-size:var(--link-large-icon-size)}[_nghost-%COMP%]:not(.nx-link--text)     .c-icon, [_nghost-%COMP%]:not(.nx-link--text)     nx-icon.nx-link__icon{margin:0 8px 0 0}[dir=rtl]   [_nghost-%COMP%]:not(.nx-link--text)     .c-icon, [dir=rtl]   [_nghost-%COMP%]:not(.nx-link--text)     nx-icon.nx-link__icon{margin:0 0 0 8px}[_nghost-%COMP%]:not(.nx-link--text).nx-link--icon-right     .c-icon, [_nghost-%COMP%]:not(.nx-link--text).nx-link--icon-right     nx-icon.nx-link__icon{order:2;margin:0 0 0 8px}[dir=rtl]   [_nghost-%COMP%]:not(.nx-link--text).nx-link--icon-right     .c-icon, [dir=rtl]   [_nghost-%COMP%]:not(.nx-link--text).nx-link--icon-right     nx-icon.nx-link__icon{margin:0 8px 0 0}.nx-link--icon-only[_nghost-%COMP%]     .c-icon, .nx-link--icon-only[_nghost-%COMP%]     nx-icon.nx-link__icon{margin-right:0}[dir=rtl]   .nx-link--icon-only[_nghost-%COMP%]     .c-icon, [dir=rtl]   .nx-link--icon-only[_nghost-%COMP%]     nx-icon.nx-link__icon{margin-left:0}[_nghost-%COMP%]  a[disabled], [_nghost-%COMP%]  a[aria-disabled]{cursor:not-allowed;color:var(--link-disabled-color)}[_nghost-%COMP%]  a[disabled]:focus, [_nghost-%COMP%]  a[aria-disabled]:focus{outline:none;box-shadow:none}@media screen and (forced-colors: active){[_nghost-%COMP%]{color:LinkText!important}}@media screen and (forced-colors: active){[_nghost-%COMP%]  a[disabled], [_nghost-%COMP%]  a[aria-disabled]{color:GrayText!important}}"],
      changeDetection: 0
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NxLinkComponent, [{
    type: Component,
    args: [{
      selector: 'nx-link',
      changeDetection: ChangeDetectionStrategy.OnPush,
      template: '<ng-content></ng-content>',
      inputs: ['classNames: nxStyle'],
      host: {
        '[class.nx-link--xsmall]': 'this.size === "xsmall"',
        '[class.nx-link--small]': 'this.size === "small"',
        '[class.nx-link--large]': 'this.size === "large"'
      },
      standalone: true,
      styles: [":host{color:var(--link-color);display:inline-block;cursor:pointer;position:relative;text-decoration:none;transition:color .2s ease,box-shadow .2s ease;hyphens:none}:host:hover{color:var(--link-hover-color)}:host::ng-deep a:active{color:var(--link-active-color)}:host::ng-deep a{display:flex;align-items:flex-start}:host.nx-link--xsmall{font-size:var(--link-xsmall-font-size);line-height:var(--link-xsmall-line-height);font-weight:var(--link-xsmall-font-weight);letter-spacing:var(--link-xsmall-letter-spacing)}:host.nx-link--small{font-size:var(--link-small-font-size);line-height:var(--link-small-line-height);font-weight:var(--link-small-font-weight);letter-spacing:var(--link-small-letter-spacing)}:host.nx-link--large{font-size:var(--link-large-font-size);line-height:var(--link-large-line-height);font-weight:var(--link-large-font-weight);letter-spacing:var(--link-large-letter-spacing)}:host.cdk-keyboard-focused ::ng-deep a{border-radius:4px;outline:0;box-shadow:var(--focus-box-shadow)}@media screen and (forced-colors: active),(forced-colors: active){:host.cdk-keyboard-focused ::ng-deep a{box-shadow:0 0 0 2px background,0 0 0 6px CanvasText;outline:4px solid CanvasText;outline-offset:2px}}:host(.nx-link--negative){color:var(--negative)}:host(.nx-link--negative):hover{color:var(--negative-01)}:host(.nx-link--negative)::ng-deep a:active{color:var(--negative-02)}:host(.nx-link--negative)::ng-deep a[disabled],:host(.nx-link--negative)::ng-deep a[aria-disabled]{color:var(--negative-dimmed)}:host(.nx-link--black){color:var(--link-black-color)}:host(.nx-link--black):hover{color:var(--link-black-hover-color)}:host(.nx-link--black)::ng-deep a:active{color:var(--link-black-active-color)}:host(.nx-link--black)::ng-deep a[disabled],:host(.nx-link--black)::ng-deep a[aria-disabled]{color:var(--link-black-disabled-color)}:host.nx-link--text{font-weight:700;text-transform:none;letter-spacing:normal;border-radius:4px;font-size:inherit;line-height:inherit;display:inline}:host.nx-link--text ::ng-deep a{display:inline}:host.nx-link--text ::ng-deep nx-icon.nx-link__icon{vertical-align:bottom}:host::ng-deep .c-icon:before,:host::ng-deep nx-icon.nx-link__icon:before{line-height:inherit;font-size:inherit}:host.nx-link--xsmall ::ng-deep .c-icon,:host.nx-link--xsmall ::ng-deep nx-icon.nx-link__icon{font-size:var(--link-xsmall-icon-size)}:host.nx-link--small ::ng-deep .c-icon,:host.nx-link--small ::ng-deep nx-icon.nx-link__icon{font-size:var(--link-small-icon-size)}:host.nx-link--large ::ng-deep .c-icon,:host.nx-link--large ::ng-deep nx-icon.nx-link__icon{font-size:var(--link-large-icon-size)}:host:not(.nx-link--text) ::ng-deep .c-icon,:host:not(.nx-link--text) ::ng-deep nx-icon.nx-link__icon{margin:0 8px 0 0}[dir=rtl] :host:not(.nx-link--text) ::ng-deep .c-icon,[dir=rtl] :host:not(.nx-link--text) ::ng-deep nx-icon.nx-link__icon{margin:0 0 0 8px}:host:not(.nx-link--text).nx-link--icon-right ::ng-deep .c-icon,:host:not(.nx-link--text).nx-link--icon-right ::ng-deep nx-icon.nx-link__icon{order:2;margin:0 0 0 8px}[dir=rtl] :host:not(.nx-link--text).nx-link--icon-right ::ng-deep .c-icon,[dir=rtl] :host:not(.nx-link--text).nx-link--icon-right ::ng-deep nx-icon.nx-link__icon{margin:0 8px 0 0}:host.nx-link--icon-only ::ng-deep .c-icon,:host.nx-link--icon-only ::ng-deep nx-icon.nx-link__icon{margin-right:0}[dir=rtl] :host.nx-link--icon-only ::ng-deep .c-icon,[dir=rtl] :host.nx-link--icon-only ::ng-deep nx-icon.nx-link__icon{margin-left:0}:host::ng-deep a[disabled],:host::ng-deep a[aria-disabled]{cursor:not-allowed;color:var(--link-disabled-color)}:host::ng-deep a[disabled]:focus,:host::ng-deep a[aria-disabled]:focus{outline:none;box-shadow:none}@media screen and (forced-colors: active){:host{color:LinkText!important}}@media screen and (forced-colors: active){:host::ng-deep a[disabled],:host::ng-deep a[aria-disabled]{color:GrayText!important}}\n"]
    }]
  }], () => [{
    type: i0.ElementRef
  }, {
    type: i0.Renderer2
  }, {
    type: i0.ChangeDetectorRef
  }, {
    type: i1.FocusMonitor
  }], {
    icon: [{
      type: ContentChild,
      args: [NxIconComponent]
    }],
    size: [{
      type: Input
    }]
  });
})();
class NxLinkModule {
  static {
    this.ɵfac = function NxLinkModule_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NxLinkModule)();
    };
  }
  static {
    this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: NxLinkModule,
      imports: [NxLinkComponent],
      exports: [NxLinkComponent]
    });
  }
  static {
    this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({});
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NxLinkModule, [{
    type: NgModule,
    args: [{
      imports: [NxLinkComponent],
      exports: [NxLinkComponent]
    }]
  }], null, null);
})();

/**
 * Generated bundle index. Do not edit.
 */

export { NxLinkComponent, NxLinkModule };
